import { computed, ref, watch } from "vue";
import { useEventListener, watchDebounced } from "@vueuse/core";
import { useRouter } from "vue-router";
import { BookOpen, ChartBar, File, FilePlus, Link, Menu, Pen, Send, Settings, SlidersHorizontal, LayoutGrid, Users } from "lucide-vue-next";
import AiStar from "@/components/episto-ui/ai/AiStar.vue";
import { isRouteAllowed } from "@/routes/guards";
import useMyFetch from "@stores/useMyFetch.js";
import { SURVEY_STATUS_VARIANTS, surveyStatusClasses } from "@/components/Survey/surveyStatus";

const EDIT_STATUSES = ["draft", "review", "paused"];

// All navigation palette pages — order here defines display order.
// Add navIcon to a route's meta in routes.js to include it here.
const NAV_PAGES = [
  { name: "SurveySetup", icon: Menu },
  { name: "SurveyContext", icon: BookOpen },
  { name: "SurveyQuestions", icon: Pen, hash: "#edit" },
  { name: "SurveyProfiles", icon: Users },
  { name: "SurveyShare", icon: Link },
  { name: "SurveyMailingLists", icon: Send, label: "Emailing" },
  { name: "SurveyProgress", icon: SlidersHorizontal, label: "Monitor" },
  { name: "SurveyResultsV2", icon: ChartBar },
  { name: "SurveyAssistant", icon: AiStar },
  { name: "SurveyGrids", icon: LayoutGrid, requiresAiChat: true },
  { name: "SurveyReportsV2", icon: File },
  { name: "ReportV2New", icon: FilePlus, label: "Reports — New", searchOnly: true },
  { name: "SurveyResultsSetup", icon: Settings },
];

export const STATUS_CLASSES = Object.fromEntries(
  Object.keys(SURVEY_STATUS_VARIANTS).map((status) => [status, surveyStatusClasses(status)]),
);

export function useNavigationSearch() {
  const router = useRouter();

  const open = ref(false);
  const query = ref("");
  const pageQuery = ref("");
  const results = ref([]);
  const loading = ref(false);
  const expandedSurvey = ref(null);
  const selectedPageIndex = ref(0);

  const routesByName = Object.fromEntries(router.getRoutes().map((r) => [r.name, r]));

  const surveyPages = NAV_PAGES.map((page) => {
    const route = routesByName[page.name];
    return {
      label: page.label || route?.meta?.title,
      icon: page.icon,
      badge: page.badge,
      searchOnly: page.searchOnly,
      if: (survey) => {
        if (route?.meta && !isRouteAllowed(route.meta)) return false;
        if (page.requiresAiChat && !survey?.ai_chat) return false;
        return true;
      },
      getRoute: (token) => ({ name: page.name, params: { token }, hash: page.hash }),
    };
  });

  const filteredPages = computed(() => {
    const q = pageQuery.value.toLowerCase();
    return surveyPages.filter((p) => {
      if (p.if && !p.if(expandedSurvey.value)) return false;
      if (p.searchOnly && !q) return false;
      return !q || p.label?.toLowerCase().includes(q);
    });
  });

  watch(filteredPages, () => {
    selectedPageIndex.value = 0;
  });

  watch(open, (isOpen) => {
    if (!isOpen) {
      query.value = "";
      pageQuery.value = "";
      results.value = [];
      loading.value = false;
      expandedSurvey.value = null;
      selectedPageIndex.value = 0;
    }
  });

  function setInputValue(value) {
    const inputEl = document.querySelector("[data-slot='command-input']");
    if (!inputEl) return;
    inputEl.value = value;
    inputEl.dispatchEvent(new InputEvent("input", { bubbles: true }));
  }

  function getSurveyRoute(survey) {
    if (EDIT_STATUSES.includes(survey.status)) {
      return { name: "SurveyQuestions", params: { token: survey.token }, hash: "#edit" };
    }
    return { name: "SurveyResultsV2", params: { token: survey.token } };
  }

  async function searchSurveys(name) {
    loading.value = true;
    try {
      const { data, error } = await useMyFetch(`/surveys/light?name=${encodeURIComponent(name)}`).get().json();
      if (error.value) {
        console.error("Navigation search failed:", error.value);
        return;
      }
      if (data.value) results.value = data.value.surveys || [];
    } finally {
      loading.value = false;
    }
  }

  watchDebounced(
    query,
    async (val) => {
      if (expandedSurvey.value) return; // typing filters pages in page mode, not surveys
      if (val.length >= 3) {
        await searchSurveys(val);
      } else {
        results.value = [];
      }
    },
    { debounce: 300 },
  );

  function handleSelect(survey) {
    open.value = false;
    router.push(getSurveyRoute(survey)).catch((err) => console.error("Navigation failed:", err));
  }

  function handleSelectPage(page) {
    open.value = false;
    router.push(page.getRoute(expandedSurvey.value.token)).catch((err) => console.error("Navigation failed:", err));
  }

  function handleOpenChange(val) {
    open.value = val;
  }

  function expandSurvey(survey) {
    expandedSurvey.value = survey;
    pageQuery.value = "";
    selectedPageIndex.value = 0;
    setInputValue("");
  }

  function collapseToSearch() {
    expandedSurvey.value = null;
    pageQuery.value = "";
    selectedPageIndex.value = 0;
    setInputValue(query.value);
  }

  useEventListener(
    "keydown",
    (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        open.value = !open.value;
        return;
      }

      if (!open.value) return;

      if (expandedSurvey.value) {
        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            e.stopPropagation();
            selectedPageIndex.value = (selectedPageIndex.value + 1) % filteredPages.value.length;
            break;
          case "ArrowUp":
            e.preventDefault();
            e.stopPropagation();
            selectedPageIndex.value =
              (selectedPageIndex.value - 1 + filteredPages.value.length) % filteredPages.value.length;
            break;
          case "Enter":
            e.preventDefault();
            e.stopPropagation();
            if (filteredPages.value[selectedPageIndex.value]) {
              handleSelectPage(filteredPages.value[selectedPageIndex.value]);
            }
            break;
          case "Escape":
          case "Tab":
            e.preventDefault();
            e.stopPropagation();
            collapseToSearch();
            break;
        }
      } else if (e.key === "Tab" && results.value.length > 0) {
        e.preventDefault();
        const highlighted = document.querySelector("[data-highlighted][data-slot='command-item']");
        const token = highlighted?.dataset?.token;
        const survey = (token && results.value.find((s) => s.token === token)) || results.value[0];
        if (survey) expandSurvey(survey);
      }
    },
    { capture: true },
  );

  return {
    open,
    query,
    pageQuery,
    results,
    loading,
    expandedSurvey,
    selectedPageIndex,
    filteredPages,
    handleSelect,
    handleSelectPage,
    handleOpenChange,
  };
}
